import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';

function SaleRegister() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');

    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const handleChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            try {
                if (selectedOption === 'itemWise') {
                    const response = await fetchData('ReportApi', `/SaleRegister/${companyId}/${fromDate}/${toDate}`, token);
                    if (response.responseCode === '1111') {
                        setDataItem(response.response_data);
                        setDataBill([]);
                    }
                } else if (selectedOption === 'billWise') {
                    const response = await fetchData('ReportApi', `/SaleBillWise/${companyId}/${fromDate}/${toDate}`, token);
                    if (response.responseCode === '1111') {
                        setDataBill(response.response_data);
                        setDataItem([]);
                    }
                }
            } catch (error) {
                console.error('Error fetching Sale Register Details:', error);
            }
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredDataBill = dataBill.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const calculateTotalAmount = () => {
        let totalAmount = 0;
        let totqty = 0;

        if (selectedOption === 'itemWise') {
            filteredDataItem.forEach((rowData) => {
                totalAmount += parseFloat(rowData.amount);
                totqty += parseFloat(rowData.qty);
            });
        } else if (selectedOption === 'billWise') {
            filteredDataBill.forEach((rowData) => {
                totalAmount += parseFloat(rowData.cashamout) + parseFloat(rowData.cardamount) + parseFloat(rowData.upiamount);
            });
        }

        return totalAmount.toFixed(2);
    };

    const handleExportExcel = () => {
        const excelData = [];

        // Add header row
        if (selectedOption === 'itemWise') {
            const headerCaption = [`Sale Register (Item Wise) From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`];
            excelData.push(headerCaption);
        } else if (selectedOption === 'billWise') {
            const headerCaption = [`Sale Register (Bill Wise)  From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`];
            excelData.push(headerCaption);
        }

        if (selectedOption === 'itemWise') {
            const headerRow = ['Sl.No', 'Item Name', 'Quantity', 'Rate', 'Amount'];
            excelData.push(headerRow);
        } else if (selectedOption === 'billWise') {
            const headerRow = ['Sl.No', 'Date', 'Sale Details', 'Cash Amount', 'Card Amount', 'UPI Amount'];
            excelData.push(headerRow);
        }

        // Add data rows
        if (selectedOption === 'itemWise') {
            filteredDataItem.forEach((rowData, index) => {
                const dataRow = [index + 1, rowData.itemname, rowData.qty, rowData.salerate, rowData.amount];
                excelData.push(dataRow);
            });
        } else if (selectedOption === 'billWise') {
            filteredDataBill.forEach((rowData, index) => {
                const dataRow = [index + 1, formatDate(rowData.date1), rowData.billno, rowData.cashamout, rowData.cardamount, rowData.upiamount];
                excelData.push(dataRow);
            });
        }

        // Add total row

        if (selectedOption === 'itemWise') {
            const totalRow = ['Total', ''];
            totalRow.push(filteredDataItem.reduce((totqty, item) => totqty + parseFloat(item.qty), 0).toFixed(2));
            totalRow.push('');
            totalRow.push(filteredDataItem.reduce((total, item) => total + parseFloat(item.amount), 0).toFixed(2));
            excelData.push(totalRow);
        } else if (selectedOption === 'billWise') {
            const totalRow = ['Total', '', '', ''];
            totalRow.push(filteredDataBill.reduce((total, item) => total + parseFloat(item.cashamout), 0).toFixed(2));
            totalRow.push(filteredDataBill.reduce((total, item) => total + parseFloat(item.cardamount), 0).toFixed(2));
            totalRow.push(filteredDataBill.reduce((total, item) => total + parseFloat(item.upiamount), 0).toFixed(2));
            excelData.push(totalRow);
        }


        const ws = XLSX.utils.aoa_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sale Register');
        XLSX.writeFile(wb, 'SaleRegister.xlsx');
    };


    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write('<html><head><title>Sale Register</title></head><body>');

        const title = selectedOption === 'itemWise' ? 'Sale Register (Item Wise)' : 'Sale Register (Bill Wise)';
        printWindow.document.write(`<h3 style="font-family: Times New Roman; font-size: 32px;">${title}</h3>`);
        printWindow.document.write(`<h4 style="font-family: Times New Roman; font-size: 22px;">From : ${formatDate(fromDate)} To : ${formatDate(toDate)}</h4>`);
        printWindow.document.write('<table style="border-collapse: collapse; width: 100%;">');
        printWindow.document.write('<thead style="background-color: #f3f3f3;">');
        printWindow.document.write('<tr>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Sl.No</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Item Name</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Quantity</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Rate</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Amount</th>');
        printWindow.document.write('</tr>');
        printWindow.document.write('</thead>');
        printWindow.document.write('<tbody>');

        if (selectedOption === 'itemWise') {
            filteredDataItem.forEach((rowData, index) => {
                printWindow.document.write('<tr>');
                printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${index + 1}</td>`);
                printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.itemname}</td>`);
                printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.qty}</td>`);
                printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.salerate}</td>`);
                printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.amount}</td>`);
                printWindow.document.write('</tr>');
            });
        }

        printWindow.document.write('</tbody>');
        printWindow.document.write('</table>');
        printWindow.document.write(`<p style="font-family: Times New Roman; font-size: 19px; text-align: right; font-weight: bolder; color: black;">Total Amount: ${calculateTotalAmount()}</p>`);
        printWindow.document.write('</body></html>');

        printWindow.document.close();
        printWindow.print();
    };

    const [isFocused, setIsFocused] = useState(false);

    return (
        <div className="w-full max-w-sm container mt-6 mx-auto">
            {/* <div className='w-full mb-2 flex'>
                <h5 className='psheading flex-grow linkcolor' style={{ fontFamily: 'Times New Roman', color: 'black' }}>SALE REGISTER</h5> */}
            {/* <div className="mr-2 text-white" style={{ display: 'flex', alignItems: 'center' }}>
                    <RadioGroup
                        aria-label="viewOption"
                        name="viewOption"
                        value={selectedOption}
                        onChange={handleChange}
                        style={{ marginLeft: '10px', display: 'flex', flexDirection: 'row' }} // Set flexDirection to 'row'
                    >
                        <FormControlLabel value="itemWise" control={<Radio />} className='text-white' label="Item Wise" />
                        <FormControlLabel value="billWise" control={<Radio />} className='text-white' label="Bill Wise" />
                    </RadioGroup>
                </div> */}



            {/* <div className='w-full mb-2 flex'>

                    <div className="mr-2" style={{ maxWidth: '150px' }}>
                        <label className="block tracking-wide text-white text-xs font-bold mb-2" htmlFor="fromDate" style={{ fontSize: '16px' }}>
                            FromDate
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}

                        />
                    </div>
                    <div className="mr-2" style={{ maxWidth: '150px' }}>
                        <label className="block tracking-wide text-white text-xs font-bold mb-2" htmlFor="toDate" style={{ fontSize: '16px' }}>
                            ToDate
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}

                        />
                    </div>
                </div>


                <div className='w-full mb-2 flex'>
                    <button
                        onClick={fetchTableData}
                        style={{
                            marginLeft: '10px',
                            cursor: 'pointer',
                            backgroundColor: '#00D100',
                            color: 'white',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '16px',
                            height: '45px',
                        }}
                    >
                        Refresh
                    </button>
                    <button
                        onClick={handleExportExcel}
                        style={{
                            marginLeft: '10px',
                            cursor: 'pointer',
                            backgroundColor: '#009688',
                            color: 'white',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '16px',
                            height: '45px',
                        }}
                    >
                        Excel
                    </button>

                    <button
                        onClick={handlePrint}
                        style={{
                            marginLeft: '10px',
                            cursor: 'pointer',
                            backgroundColor: '#ff5733',
                            color: 'white',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '16px',
                            height: '45px',
                        }}
                    >
                        Print
                    </button>
                    <div style={{ marginBottom: '10px', marginLeft: '20px', display: 'flex', alignItems: 'center' }}>

                        <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                        {showSearchInput && (
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearch}
                                style={{ marginLeft: '10px' }}
                            />
                        )}
                    </div>

                </div>
            </div> */}
            <div className='w-full mb-2 flex'>
                <h5 className='psheading flex-grow linkcolor' style={{ fontFamily: 'Times New Roman', color: 'black' }}>SALE REGISTER</h5>                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <div className="mr-2 text-white" style={{ display: 'flex', alignItems: 'center' }}>
                        <RadioGroup
                            aria-label="viewOption"
                            name="viewOption"
                            value={selectedOption}
                            onChange={handleChange}
                            style={{ marginLeft: '10px', display: 'flex', flexDirection: 'row' }} // Set flexDirection to 'row'
                        >
                            <FormControlLabel value="itemWise" control={<Radio />} className='text-black' label="Item Wise" />
                            <FormControlLabel value="billWise" control={<Radio />} className='text-black' label="Bill Wise" />
                        </RadioGroup>
                    </div>
                    <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>

                        <input
                            className="shadow text-gray-700 leading-tight"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            style={{
                                width: isFocused ? '300px' : '200px',
                                height: '40px',
                                border: '1px solid #ccc',
                                borderRadius: '15px',
                                padding: '0 20px',
                                transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                                outline: 'none',
                                borderColor: isFocused ? 'darkcyan' : '#ccc',
                                boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                                marginTop: '22px'
                            }}
                        />
                    </div>
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        FromDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}

                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        ToDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}

                    />
                </div>
                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '20px' }}
                    onClick={fetchTableData}
                    className="linkcolor"
                >

                    <span style={{ color: 'darkgreen', fontWeight: 'bolder', marginTop: '-5px' }}>REFRESH</span>
                </Button>

                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '20px' }}
                    onClick={handleExportExcel}
                    className="linkcolor"
                >

                    <span style={{ color: 'darkblue', fontWeight: 'bolder', marginTop: '-5px' }}>EXCEL</span>
                </Button>

                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '20px' }}
                    onClick={handlePrint}
                    className="linkcolor"
                >

                    <span style={{ color: 'orange', fontWeight: 'bolder', marginTop: '-5px' }}>PRINT</span>
                </Button>


            </div>

            <div className='w-full mb-5 flex' style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {selectedOption === 'itemWise' && (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#f3f3f3' }}>
                                <TableRow>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Sl.No</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Item Name</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Quantity</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Rate</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDataItem.map((data, index) => (
                                    <TableRow key={data.id}>
                                        <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{index + 1}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{data.itemname}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{data.qty}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{data.salerate}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{data.amount}</TableCell>
                                    </TableRow>
                                ))}
                                {/* Total Row */}
                                <TableRow>
                                    <TableCell colSpan={1} />
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px', fontWeight: 'bolder', color: 'black' }}>Total</TableCell>
                                    {/* <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}></TableCell> */}
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px', fontWeight: 'bolder', color: 'black' }}>
                                        {filteredDataItem.reduce((totqty, item) => totqty + parseFloat(item.qty), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell>

                                    </TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px', fontWeight: 'bolder', color: 'black' }}>
                                        {filteredDataItem.reduce((total, item) => total + parseFloat(item.amount), 0).toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {selectedOption === 'billWise' && (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#f3f3f3' }}>
                                <TableRow>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Sl.No</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Date</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Bill Details</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Cash Amount</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Card Amount</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>UPI Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDataBill.map((data, index) => (
                                    <TableRow key={data.id}>
                                        <TableCell sx={{ fontSize: '15px', height: '40px' }}>{index + 1}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '40px' }}>{formatDate(data.date1)}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '40px' }}>{data.billno}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '40px' }}>{data.cashamout}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '40px' }}>{data.cardamount}</TableCell>
                                        <TableCell sx={{ fontSize: '15px', height: '40px' }}>{data.upiamount}</TableCell>
                                    </TableRow>
                                ))}
                                {/* Total Row */}
                                <TableRow>
                                    <TableCell colSpan={2} />
                                    <TableCell sx={{ fontSize: '15px', height: '40px', padding: '11px', fontWeight: 'bolder', color: 'black' }}>Total</TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '40px', padding: '11px' }}>
                                        {filteredDataBill.reduce((total, item) => total + parseFloat(item.cashamout), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '40px', padding: '11px' }}>
                                        {filteredDataBill.reduce((total, item) => total + parseFloat(item.cardamount), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '40px', padding: '11px' }}>
                                        {filteredDataBill.reduce((total, item) => total + parseFloat(item.upiamount), 0).toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

            </div>
        </div>
    );
}

export default SaleRegister;

